import type { SpeedMonitoringSettings } from '~/composables/common/useSystemConfig';
import type { GeolocationAcquisitionError } from '~/stores/geolocations/geolocationStore';
import { distanceCalculator } from '~/utils/distanceCalculator';

/**
 * 速度判定に必要な入力情報。
 *
 * composable/storeに依存せず、速度計算と閾値判定に必要な値だけを受け取る。
 */
export type SpeedJudgementInput = {
  /** 直近取得した現在位置。初回取得待ちや取得失敗時はnull */
  position: GeolocationPosition | null;
  /** 位置情報取得時に発生したエラー情報。初回取得待ちや正常時はnull */
  acquisitionError: GeolocationAcquisitionError | null;
  /** 速度算出に直前使用した位置情報。初回判定時はnull */
  previousSamplePosition: GeolocationPosition | null;
  /** 連続でGPSエラーが発生した回数 */
  consecutiveGpsErrorCount: number;
  /** 現在速度超過状態として確定しているか */
  isSpeedExceeded: boolean;
  /** 速度超過方向（速度が上限閾値超）の連続判定回数 */
  consecutiveExceededCount: number;
  /** 停止方向（速度が下限閾値未満）の連続判定回数 */
  consecutiveStopCount: number;
  /** 現在GPSエラー状態として扱われているか */
  isGpsError: boolean;
  /** 現在のGPSエラー状態の要因となったエラー種別。GPSエラーでない場合はnull */
  gpsErrorType: GeolocationAcquisitionError['type'] | null;
  /** 速度監視のシステム設定 */
  settings: SpeedMonitoringSettings;
};

/**
 * 速度判定の結果。
 *
 * composable側はこの結果を元にローカル状態更新、ログ出力、store更新を行う。
 */
export type SpeedJudgementResult = {
  /** 判定結果の分類 */
  status: 'waiting' | 'gps-error' | 'accuracy-skipped' | 'judged';
  /** 次回の速度算出に使用する位置情報 */
  nextPreviousSamplePosition: GeolocationPosition | null;
  /** 次回判定へ引き継ぐGPSエラー連続回数 */
  nextConsecutiveGpsErrorCount: number;
  /** 次回判定へ引き継ぐ速度超過方向の連続回数 */
  nextConsecutiveExceededCount: number;
  /** 次回判定へ引き継ぐ停止方向の連続回数 */
  nextConsecutiveStopCount: number;
  /** geolocationStoreの速度監視関連フィールドへ反映する値 */
  speedFields?: {
    /** 直近算出した速度（m/s） */
    currentSpeed?: number;
    /** 速度超過状態として確定しているか */
    isSpeedExceeded?: boolean;
    /** GPSエラー状態かどうか */
    isGpsError?: boolean;
    /** GPSエラー状態の要因となったエラー種別。GPSエラー解除時はnull */
    gpsErrorType?: GeolocationAcquisitionError['type'] | null;
  };
  /** 精度不足で判定をスキップした場合の位置精度（メートル） */
  skippedAccuracy?: number;
  /** ログ出力用の直近算出速度（m/s） */
  currentSpeed?: number;
};

/**
 * 速度監視の判定を行い、storeとローカル状態へ反映する値を返す。
 *
 * @param input 速度判定に必要な位置情報、前回サンプル、速度履歴、設定値
 * @returns 判定結果、次回へ引き継ぐローカル状態、store更新値、ログ出力用情報
 */
export const judgeSpeedPosition = ({
  position,
  acquisitionError,
  previousSamplePosition,
  consecutiveGpsErrorCount,
  isSpeedExceeded,
  consecutiveExceededCount,
  consecutiveStopCount,
  isGpsError,
  gpsErrorType,
  settings,
}: SpeedJudgementInput): SpeedJudgementResult => {
  // 実際に位置情報取得エラーが発生している場合のみ、GPSエラー連続回数を加算する。
  // 速度の連続カウンタは信頼できる速度が得られないため、状態を変えずに次回へ素通しする。
  if (acquisitionError !== null) {
    const nextConsecutiveGpsErrorCount = consecutiveGpsErrorCount + 1;
    const shouldSetGpsError
      = nextConsecutiveGpsErrorCount > settings.gpsFailedCountThreshold && !isGpsError;

    // GPSエラー確定時はエラー種別を併せて反映する。
    // 確定済みで種別だけが変わった場合（例: 警告抑制中に位置情報権限をOFFにした）も
    // 追従して更新し、抑制可否の判定が実際の要因とずれないようにする。
    let speedFields: SpeedJudgementResult['speedFields'];
    if (shouldSetGpsError) {
      speedFields = { isGpsError: true, gpsErrorType: acquisitionError.type };
    }
    else if (isGpsError && gpsErrorType !== acquisitionError.type) {
      speedFields = { gpsErrorType: acquisitionError.type };
    }

    return {
      status: 'gps-error',
      nextPreviousSamplePosition: previousSamplePosition,
      nextConsecutiveGpsErrorCount,
      nextConsecutiveExceededCount: consecutiveExceededCount,
      nextConsecutiveStopCount: consecutiveStopCount,
      speedFields,
    };
  }

  // 位置未取得かつエラーなしの場合は、watchPositionの初回取得待ちとして何もしない。
  if (position === null) {
    return {
      status: 'waiting',
      nextPreviousSamplePosition: previousSamplePosition,
      nextConsecutiveGpsErrorCount: consecutiveGpsErrorCount,
      nextConsecutiveExceededCount: consecutiveExceededCount,
      nextConsecutiveStopCount: consecutiveStopCount,
    };
  }

  // 位置取得に成功した場合は、必要に応じてGPSエラー状態（種別含む）を解除する。
  const clearedGpsErrorFields = isGpsError ? { isGpsError: false, gpsErrorType: null } : {};

  // 精度が設定値を超える場合は、速度計算に使わず判定をスキップする。
  // 信頼できない速度なので連続カウンタは状態維持（素通し）する。
  if (position.coords.accuracy > settings.accuracyThreshold) {
    return {
      status: 'accuracy-skipped',
      nextPreviousSamplePosition: previousSamplePosition,
      nextConsecutiveGpsErrorCount: 0,
      nextConsecutiveExceededCount: consecutiveExceededCount,
      nextConsecutiveStopCount: consecutiveStopCount,
      speedFields: clearedGpsErrorFields,
      skippedAccuracy: position.coords.accuracy,
    };
  }

  // 速度算出ではGeolocation APIのspeedプロパティを優先し、無い場合のみ距離/時間で補完する。
  let speed: number;
  if (position.coords.speed !== null && position.coords.speed !== undefined) {
    speed = position.coords.speed;
  }
  // 直前サンプルがある場合は、2点間距離とタイムスタンプ差分から速度を算出する。
  else if (previousSamplePosition) {
    const distance = distanceCalculator.calculate(
      previousSamplePosition.coords.latitude,
      previousSamplePosition.coords.longitude,
      position.coords.latitude,
      position.coords.longitude,
    );
    const timeDiff = (position.timestamp - previousSamplePosition.timestamp) / 1000;
    speed = timeDiff > 0 ? (distance / timeDiff) : 0;
  }
  // 初回サンプルは移動量を算出できないため速度0として扱う。
  else {
    speed = 0;
  }
  speed = Math.abs(speed);

  // 瞬時速度（currentSpeed）に対し、連続N回 + ヒステリシスで超過/停止を判定する。
  // 設定値（km/h）をm/sへ変換。超過は上限閾値、停止（解除）は下限閾値を用いる。
  const exceededThresholdMs = settings.exceededSpeedThreshold / 3.6;
  const stopThresholdMs = settings.stopSpeedThreshold / 3.6;

  let nextIsSpeedExceeded = isSpeedExceeded;
  let nextConsecutiveExceededCount = consecutiveExceededCount;
  let nextConsecutiveStopCount = consecutiveStopCount;

  if (!isSpeedExceeded) {
    // 通常時：上限閾値超が連続 exceededCountThreshold 回で速度超過を確定する。
    nextConsecutiveStopCount = 0;
    nextConsecutiveExceededCount = speed > exceededThresholdMs ? consecutiveExceededCount + 1 : 0;
    if (nextConsecutiveExceededCount >= settings.exceededCountThreshold) {
      nextIsSpeedExceeded = true;
      nextConsecutiveExceededCount = 0;
    }
  }
  else {
    // 超過時：下限閾値未満が連続 stopCountThreshold 回で速度超過を解除する。
    nextConsecutiveExceededCount = 0;
    nextConsecutiveStopCount = speed < stopThresholdMs ? consecutiveStopCount + 1 : 0;
    if (nextConsecutiveStopCount >= settings.stopCountThreshold) {
      nextIsSpeedExceeded = false;
      nextConsecutiveStopCount = 0;
    }
  }

  return {
    status: 'judged',
    nextPreviousSamplePosition: position,
    nextConsecutiveGpsErrorCount: 0,
    nextConsecutiveExceededCount,
    nextConsecutiveStopCount,
    speedFields: {
      ...clearedGpsErrorFields,
      currentSpeed: speed,
      isSpeedExceeded: nextIsSpeedExceeded,
    },
    currentSpeed: speed,
  };
};
